import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError } from "../../../shared/api/client";
import { AppBadge, AppButton } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import {
  IconCheck,
  IconError,
  IconHourglass,
  IconInfo,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import { getPayment, regenerateToken, resendTokenSms } from "../api";
import { formatDate, formatTime, formatTzs, groupTokenDigits } from "../format";
import type { Payment, PaymentToken, TokenDeliveryState } from "../types";

/**
 * S4 — Token delivery (handoff TASK-009, s4-token-delivery-* boards).
 * Two end states:
 *   (a) time-bound token — OpenPAYGO grouped digits, plan-configured days,
 *       full token shown on the recording device only (masked on re-read);
 *   (b) permanent unlock — fully-paid (24/24-style) rule, restrained success
 *       panel with ownership-certificate affordance.
 * Device lifecycle pending → pushed → confirmed-by-device; SMS indicator
 * with resend on failure. Issuance is idempotent per allocation — retry and
 * regenerate can never double-issue.
 */

function deviceStateBadge(state: TokenDeliveryState) {
  if (state === "confirmed")
    return (
      <AppBadge tone="success">
        <IconCheck size={12} /> Confirmed by device
      </AppBadge>
    );
  if (state === "pushed")
    return (
      <AppBadge tone="info">
        <IconHourglass size={12} /> Pushed — awaiting device confirmation
      </AppBadge>
    );
  return (
    <AppBadge tone="neutral">
      <IconHourglass size={12} /> Queued for device push
    </AppBadge>
  );
}

export function S4TokenDelivery() {
  const { id = "" } = useParams();
  const paymentId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Stable per mount — regenerate replay returns the same token.
  const [regenKey] = useState(() => crypto.randomUUID());

  const query = useQuery({
    queryKey: ["payment", paymentId],
    queryFn: () => getPayment(paymentId),
    retry: 1,
    refetchInterval: (q) => {
      const p = q.state.data?.payment;
      if (p && p.status === "verified" && p.allocation && !p.token) return 3_000;
      // Keep watching until the device confirms.
      if (p?.token && p.token.state !== "confirmed") return 10_000;
      return false;
    },
  });

  const payment: Payment | undefined = query.data?.payment;
  useFieldHeader(
    "Token delivery",
    payment
      ? `${payment.customer_name ?? "Customer"} — ${payment.contract_number ?? payment.payment_number}`
      : undefined,
  );
  useFieldJourney({ flow: "payments", step: 3, backTo: `/field/payments/${id}/allocation` });

  const resend = useMutation({ mutationFn: () => resendTokenSms(paymentId) });
  const regen = useMutation({
    mutationFn: () => regenerateToken(paymentId, regenKey),
    onSuccess: (data) => {
      // Fold the freshly issued (full) token into the cached payment.
      queryClient.setQueryData(["payment", paymentId], (old: { payment: Payment } | undefined) =>
        old ? { payment: { ...old.payment, token: data.token } } : old,
      );
    },
  });

  if (query.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Generating token…</h2>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Token generation failed</h2>
        <p>
          Token issuance is idempotent per allocation — retrying returns the same token, never a
          second one.
        </p>
        <AppButton variant="secondary" type="button" onClick={() => void query.refetch()}>
          Retry
        </AppButton>
      </div>
    );
  }

  if (!payment || !payment.token) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Generating token…</h2>
        <p>Issued once per allocation. This screen updates automatically.</p>
      </div>
    );
  }

  const token: PaymentToken = payment.token;

  return (
    <>
      <div className="pg-verify-summary">
        <AppBadge tone="success">
          <IconCheck size={12} /> Payment verified
        </AppBadge>
        <span className="vs-meta">
          {payment.payment_number} · {formatTzs(payment.amount_cents)}
        </span>
      </div>

      {token.kind === "permanent_unlock" && token.permanent_unlock ? (
        <PermanentUnlockPanel token={token} />
      ) : (
        <TimeBoundPanel
          token={token}
          onRegenerate={() => regen.mutate()}
          regenBusy={regen.isPending}
          regenError={
            regen.isError
              ? regen.error instanceof ApiError
                ? regen.error.message
                : "network error"
              : null
          }
        />
      )}

      <div className="pg-verify-summary">{deviceStateBadge(token.state)}</div>

      <SmsCard
        token={token}
        onResend={() => resend.mutate()}
        resendBusy={resend.isPending}
        resendDone={resend.isSuccess}
        resendError={
          resend.isError
            ? resend.error instanceof ApiError
              ? resend.error.message
              : "network error"
            : null
        }
      />

      <div className="pg-actions">
        <AppButton
          type="button"
          onClick={() => navigate(`/field/customers/${payment.customer_id}/payment-timeline`)}
        >
          Done — view timeline
        </AppButton>
      </div>
    </>
  );
}

// ── Time-bound token panel ───────────────────────────────────────────────────

function TimeBoundPanel({
  token,
  onRegenerate,
  regenBusy,
  regenError,
}: {
  token: PaymentToken;
  onRegenerate: () => void;
  regenBusy: boolean;
  regenError: string | null;
}) {
  return (
    <>
      <div className="pg-token-panel">
        <div className="token-caption">
          {token.valid_days ?? "—"} activation days — OpenPAYGO token
        </div>
        {token.token ? (
          <div className="token-digits">{groupTokenDigits(token.token)}</div>
        ) : (
          <>
            <div className="token-digits">{groupTokenDigits(token.token_masked)}</div>
            <div className="token-caption">
              Full token was shown on the recording device — masked here.
            </div>
          </>
        )}
        <div className="token-validity">
          Valid {token.valid_days ?? "—"} days
          {token.valid_until ? ` · until ${formatDate(token.valid_until)}` : ""} · customer enters
          it on the device keypad
        </div>
      </div>
      {regenError ? (
        <div className="pg-inline-error" role="alert">
          <IconError size={14} /> Regenerate failed — {regenError}. The original token is unchanged.
        </div>
      ) : null}
      <AppButton variant="secondary" type="button" onClick={onRegenerate} disabled={regenBusy}>
        {regenBusy ? <IconSpinner size={14} /> : null} Regenerate token
      </AppButton>
    </>
  );
}

// ── Permanent unlock panel (fully paid) ──────────────────────────────────────

function PermanentUnlockPanel({ token }: { token: PaymentToken }) {
  const unlock = token.permanent_unlock!;
  return (
    <div className="pg-unlock-panel">
      <IconCheck size={32} />
      <div className="up-title">Device permanently unlocked</div>
      <div className="up-sub">
        Fully paid — {unlock.payments_made} of {unlock.payments_total} payments. No more tokens
        needed.
      </div>
      <AppButton variant="secondary" type="button" onClick={() => window.print()}>
        Ownership certificate
      </AppButton>
      <div className="up-meta">
        Unlocked {formatDate(unlock.unlocked_at)} · cert {unlock.certificate_number}
      </div>
    </div>
  );
}

// ── SMS indicator + resend ───────────────────────────────────────────────────

function SmsCard({
  token,
  onResend,
  resendBusy,
  resendDone,
  resendError,
}: {
  token: PaymentToken;
  onResend: () => void;
  resendBusy: boolean;
  resendDone: boolean;
  resendError: string | null;
}) {
  const sms = token.sms;
  if (!sms) return null;

  const failed = sms.state === "failed" && !resendDone;
  const label =
    token.kind === "permanent_unlock" ? "Ownership confirmation" : "Token delivery";

  return (
    <>
      <div className={`pg-sms-card${failed ? " failed" : ""}`} role="status">
        <span className="sms-icon">
          {failed ? <IconWarning size={18} /> : <IconCheck size={18} />}
        </span>
        <span>
          <div className="sms-title">
            {failed
              ? `SMS to ${sms.to ?? "customer"} failed`
              : `SMS ${sms.state === "pending" ? "queued" : "sent"} to ${sms.to ?? "customer"}`}
          </div>
          <div className="sms-meta">
            {label}
            {sms.sent_at ? ` · today ${formatTime(sms.sent_at)}` : ""}
            {sms.state === "delivered" ? " · delivery confirmed" : ""}
          </div>
        </span>
        {failed ? (
          <AppButton variant="secondary" type="button" onClick={onResend} disabled={resendBusy}>
            {resendBusy ? <IconSpinner size={14} /> : null} Resend
          </AppButton>
        ) : null}
      </div>
      {resendError ? (
        <div className="pg-inline-error" role="alert">
          <IconError size={14} /> Resend failed — {resendError}. Retry in a moment.
        </div>
      ) : null}
      {failed ? (
        <div className="pg-idem-note">
          <IconInfo size={14} /> If SMS keeps failing, read the token to the customer from this
          screen — the device keypad accepts it either way.
        </div>
      ) : null}
    </>
  );
}
